/*eslint complexity: ["error", 27]*/
/*eslint max-lines-per-function: ["error", 230]*/
import React, { useEffect, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';
import { isEqual } from 'lodash';

import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  CreateUpdateContractDto,
  GetContractExtendedDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { ContractGeneralTab } from '../../components/Contracts/ContractGeneralTab';
import {
  convert,
  newContract,
} from '../../../common/pokCore/contexts/ContractContext';
import { ContractTabsEnum } from '../../components/Contracts/ContractTabsEnum';
import { BadCompany } from '../../../common/components/NotAuthorized/BadCompany';
import { useAuth } from '../../../common/hooks/useAuth';
import { ContractEstimateItemTab } from '../../components/Contracts/ContractEstimateItemTab';
import { buildTabTitle, getTabClassName } from '../../../utils/tabUtils';
import ContractTitle from '../../components/Contracts/ContractTitle';
import { ConcurrencySignaler } from '../../../app/components/ConcurrencySignaler';
import { DataChangeScope } from '../../../common/pokCore/sockets/socket.datatypes';
import { ContractStatusEnum } from '../../../common/pokCore/validation/enums';

type ContractViewParams = {
  contractId?: string;
  tv?: boolean;
};

export const ContractView: React.FC<ContractViewParams> = props => {
  const [id, setId] = useState<string | undefined>(props.contractId);
  const [contract, setContract] = useState<CreateUpdateContractDto>();
  const [originalContract, setOriginalContract] =
    useState<CreateUpdateContractDto>();
  const [extendedContract, setExtendedContract] =
    useState<GetContractExtendedDto>();
  const [clientLanguage, setClientLanguage] = useState<string | undefined>();
  const { t, tk } = useTranslation('projectLng');
  const [authorized, setAuthorized] = useState<boolean>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<string>();
  const [notSavedTab, setNotSavedTab] = useState<ContractTabsEnum>();
  const [key, setKey] = useState({
    scope: DataChangeScope.contract,
    id: props.contractId || '',
  });
  const [description, setDescription] = useState(
    `${t(tk.contract.contractSystemNumber)} ${contract?.systemNumber}`,
  );
  const [readyToUpdate, setReadyToUpdate] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const auth = useAuth();

  useEffect(() => {
    setId(props.contractId);
  }, [props.contractId]);

  useEffect(() => {
    if (props.contractId) {
      setKey({
        scope: DataChangeScope.contract,
        id: props.contractId,
      });
      setDescription(
        `${t(tk.contract.contractSystemNumber)} ${contract?.systemNumber}`,
      );
    }
  }, [contract, props.contractId, t, tk.contract.contractSystemNumber]);

  useEffect(() => {
    if (id) {
      pok.contracts
        .getWithAttachmentById(id)
        .then(res => {
          setContract(convert(res));
          setOriginalContract(convert(res));
          setExtendedContract(res);
          setCompanyId(res?.project?.company?.id);
          setNotSavedTab(undefined);
        })
        .catch(err => {
          notifications.caughtError(err);
        });
    }
  }, [id, notifications, pok.contracts, refresh]);

  useEffect(() => {
    if (contract?.clientId) {
      pok.clients
        .getById(contract.clientId)
        .then(res => setClientLanguage(res.language))
        .catch(() => setClientLanguage(undefined));
    } else {
      setClientLanguage(undefined);
    }
  }, [contract?.clientId, pok.clients]);

  useEffect(() => {
    if (
      id &&
      contract?.active &&
      contract?.status === ContractStatusEnum.Entered &&
      notSavedTab === undefined &&
      (extendedContract?.contractEstimateItems?.length || 0) === 0 &&
      extendedContract?.attachment?.id
    ) {
      setReadyToUpdate(true);
    } else {
      setReadyToUpdate(false);
    }
  }, [
    id,
    contract,
    notSavedTab,
    extendedContract?.contractEstimateItems,
    extendedContract?.attachment?.id,
  ]);

  useEffect(() => {
    if (companyId && companyId !== pok.getCompanyId()) {
      setAuthorized(false);
    } else {
      setAuthorized(true);
    }
  }, [pok, companyId]);

  const handleRefresh = (id?: string) => {
    setRefresh(!refresh);
    if (id) {
      setId(id);
    }
  };

  const saveSignaturePosition = async () => {
    if (id && originalContract && contract?.signaturePosition) {
      await pok.contracts.saveSignature(id, contract?.signaturePosition);
      originalContract.signaturePosition = contract?.signaturePosition;
      setOriginalContract(originalContract);
      const dataChanged = !isEqual(
        {
          ...contract,
        },
        { ...originalContract },
      );
      setNotSavedTab(dataChanged ? ContractTabsEnum.GENERAL : undefined);
    }
  };

  return (
    <>
      {!authorized && contract && companyId && (
        <BadCompany companyId={companyId} />
      )}
      {(authorized || !contract || !companyId) && (
        <Card className="main-card">
          <Card.Body className="m-3">
            <ContractTitle
              id={id}
              contract={contract}
              tv={props.tv}
              readyToUpdate={readyToUpdate}
            />

            <TabsHistory
              activeKey={ContractTabsEnum.GENERAL}
              id="contract-editor"
              className="my-3"
            >
              <Tab
                key={ContractTabsEnum.GENERAL}
                eventKey={ContractTabsEnum.GENERAL}
                title={buildTabTitle(
                  t(tk.contract.unwrittenUppercase),
                  t(tk.contract.general),
                  notSavedTab === ContractTabsEnum.GENERAL,
                )}
                className="m-3"
                tabClassName={getTabClassName(
                  notSavedTab === ContractTabsEnum.GENERAL,
                )}
              >
                <ContractGeneralTab
                  contract={
                    contract ||
                    newContract(undefined, auth.currentUser?.sub, props.tv)
                  }
                  originalContract={originalContract}
                  contractId={id}
                  attachmentId={extendedContract?.attachment?.id}
                  setContract={setContract}
                  readonly={
                    !!notSavedTab && notSavedTab !== ContractTabsEnum.GENERAL
                  }
                  setNotSavedTab={setNotSavedTab}
                  hasEstimateItemsWithAmounts={
                    (extendedContract?.contractEstimateItems?.length || 0) > 0
                  }
                  handleRefresh={handleRefresh}
                  saveSignaturePosition={saveSignaturePosition}
                  clientLanguage={clientLanguage}
                />
              </Tab>
              <Tab
                key={ContractTabsEnum.ESTIMATE_ITEM_POSITIONS}
                eventKey={ContractTabsEnum.ESTIMATE_ITEM_POSITIONS}
                className="m-3"
                disabled={!id}
                tabClassName={getTabClassName(
                  notSavedTab === ContractTabsEnum.ESTIMATE_ITEM_POSITIONS,
                )}
                title={buildTabTitle(
                  t(tk.contract.unwrittenUppercase),
                  t(tk.contract.positions),
                  notSavedTab === ContractTabsEnum.ESTIMATE_ITEM_POSITIONS,
                )}
              >
                <ContractEstimateItemTab
                  contract={
                    contract ||
                    newContract(undefined, auth.currentUser?.sub, props.tv)
                  }
                  originalContract={originalContract}
                  contractId={id}
                  setContract={setContract}
                  readonly={
                    (!!notSavedTab &&
                      notSavedTab !==
                        ContractTabsEnum.ESTIMATE_ITEM_POSITIONS) ||
                    contract?.active === false
                  }
                  setNotSavedTab={setNotSavedTab}
                  notSavedTab={notSavedTab}
                  extendedContract={extendedContract}
                  handleRefresh={handleRefresh}
                />
              </Tab>
            </TabsHistory>
            {contract && (
              <ConcurrencySignaler
                dataKey={key}
                objectDescription={description}
                editAllowed={true}
                refreshAction={handleRefresh}
              />
            )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};
